import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const DisciplinaryForm = ({
  staffName: initialStaffName,
  role: initialRole,
  staffId: initialStaffId,
  type: initialType,
  location: initialLocation,
}) => {
  const [details, setDetails] = useState({
    dateOfIncident: "",
    timeOfIncident: "",
    locationOfIncident: "",
    descriptionOfIncident: "",
    policyViolated: "",
    witnesses: [],
    witnessStatements: "",
    employeePerspective: "",
    agreeWithIncident: "",
    explanation: "",
    stepsTaken: "",
    findings: "",
    disciplinaryAction: "",
    rationale: "",
    correctiveActions: "",
    supportProvided: "",
    improvementDeadline: "",
  });
  const [staffName, setStaffName] = useState(initialStaffName);
  const [staffId, setStaffId] = useState(initialStaffId);
  const [location, setLocation] = useState(initialLocation);
  const [staff, setStaff] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await fetch(`https://api.insightos.co.uk/api/staff`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        if (response.ok) {
          const data = await response.json();
          setStaff(data.data);
        } else {
          console.error("Failed to fetch user data");
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    setStaffName(initialStaffName);
    setStaffId(initialStaffId);
    setLocation(initialLocation);
  }, [initialStaffName, initialStaffId, initialLocation]);

  const handleChange = (e) => {
    const { name, value, type, multiple, options } = e.target;
    if (type === "select-multiple") {
      const selected = Array.from(options)
        .filter((option) => option.selected)
        .map((option) => option.value);
      setDetails({ ...details, [name]: selected });
    } else {
      setDetails({ ...details, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const form = {
      DateOfIncident: details.dateOfIncident,
      TimeOfIncident: details.timeOfIncident,
      LocationOfIncident: details.locationOfIncident,
      DescriptionOfIncident: details.descriptionOfIncident,
      PolicyViolated: details.policyViolated,
      Witnesses: details.witnesses, // now an array of staff names
      WitnessStatements: details.witnessStatements,
      EmployeePerspective: details.employeePerspective,
      AgreeWithIncident: details.agreeWithIncident,
      Explanation: details.explanation,
      StepsTaken: details.stepsTaken,
      Findings: details.findings,
      DisciplinaryAction: details.disciplinaryAction,
      Rationale: details.rationale,
      CorrectiveActions: details.correctiveActions,
      SupportProvided: details.supportProvided,
      ImprovementDeadline: details.improvementDeadline,
      StaffName: staffName,
      StaffId: staffId,
      Location: location,
    };

    try {
      const response = await fetch("https://api.insightos.co.uk/api/disciplinary", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(form),
      });

      if (response.ok) {
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white shadow-md rounded-lg">
      <h2 className="text-2xl font-bold mb-4">Disciplinary Procedure Form</h2>
      <p className="text-sm text-gray-600 mb-4"> Name: {staffName} </p>
      <p className="text-sm text-gray-600 mb-4"> Location: {location} </p>
      <p className="text-sm text-gray-600 mb-4">
        Please fill out the form below to document the disciplinary procedure.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Date of Incident */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Date of Incident
          </label>
          <input
            type="date"
            name="dateOfIncident"
            value={details.dateOfIncident}
            onChange={handleChange}
            className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        {/* Time of Incident */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Time of Incident
          </label>
          <input
            type="time"
            name="timeOfIncident"
            value={details.timeOfIncident}
            onChange={handleChange}
            className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        {/* Location of Incident */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Location of Incident
          </label>
          <input
            type="text"
            name="locationOfIncident"
            value={details.locationOfIncident}
            onChange={handleChange}
            className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Enter location"
          />
        </div>

        {/* Description of Incident */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Description of Incident
          </label>
          <textarea
            name="descriptionOfIncident"
            value={details.descriptionOfIncident}
            onChange={handleChange}
            className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Provide a detailed description"
          ></textarea>
        </div>

        {/* Policy Violated */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Policy/Rule Violated
          </label>
          <textarea
            name="policyViolated"
            value={details.policyViolated}
            onChange={handleChange}
            className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Mention specific policy or rule"
          ></textarea>
        </div>

        {/* Witness Information - MULTIPLE SELECT */}
        <div>
          <label className="block text-sm font-medium mb-1">Witnesses</label>
          <select
            name="witnesses"
            multiple
            value={details.witnesses}
            onChange={handleChange}
            className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 h-32"
          >
            {staff.map((person) => (
              <option key={person.id || person.id || person.Name} value={person.Name}>
                {person.Name}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Hold Ctrl (Windows) or Cmd (Mac) to select multiple witnesses.
          </p>
        </div>

        {/* Witness Statements */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Witness Statements
          </label>
          <textarea
            name="witnessStatements"
            value={details.witnessStatements}
            onChange={handleChange}
            className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Provide witness statements (if any)"
          ></textarea>
        </div>

        {/* Employee Perspective */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Employee's Perspective
          </label>
          <textarea
            name="employeePerspective"
            value={details.employeePerspective}
            onChange={handleChange}
            className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Employee's explanation of the incident"
          ></textarea>
        </div>

        {/* Agree with Incident */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Does the employee agree with the account of the incident?
          </label>
          <select
            name="agreeWithIncident"
            value={details.agreeWithIncident}
            onChange={handleChange}
            className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">Select an option</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>

        {/* Explanation */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Explanation (if applicable)
          </label>
          <textarea
            name="explanation"
            value={details.explanation}
            onChange={handleChange}
            className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Provide explanation if disagreement exists"
          ></textarea>
        </div>

        {/* Steps Taken */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Steps Taken to Investigate
          </label>
          <textarea
            name="stepsTaken"
            value={details.stepsTaken}
            onChange={handleChange}
            className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="List investigation steps"
          ></textarea>
        </div>

        {/* Findings */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Findings of Investigation
          </label>
          <textarea
            name="findings"
            value={details.findings}
            onChange={handleChange}
            className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Summarize findings"
          ></textarea>
        </div>

        {/* Disciplinary Action */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Disciplinary Action Proposed
          </label>
          <textarea
            name="disciplinaryAction"
            value={details.disciplinaryAction}
            onChange={handleChange}
            className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Specify proposed action (e.g., warning, suspension)"
          ></textarea>
        </div>

        {/* Rationale */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Rationale for Action Taken
          </label>
          <textarea
            name="rationale"
            value={details.rationale}
            onChange={handleChange}
            className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Provide rationale for disciplinary action"
          ></textarea>
        </div>

        {/* Corrective Actions */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Corrective Actions
          </label>
          <textarea
            name="correctiveActions"
            value={details.correctiveActions}
            onChange={handleChange}
            className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="List actions employee must take to improve"
          ></textarea>
        </div>

        {/* Support Provided */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Support Provided
          </label>
          <textarea
            name="supportProvided"
            value={details.supportProvided}
            onChange={handleChange}
            className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Specify support to be provided by company"
          ></textarea>
        </div>

        {/* Improvement Deadline */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Improvement Deadline
          </label>
          <input
            type="date"
            name="improvementDeadline"
            value={details.improvementDeadline}
            onChange={handleChange}
            className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          data-component="disciplinary-form"
          data-time-saved="15"
          data-feature="submit_disciplinary_post"
          className="bg-indigo-500 text-white px-4 py-2 rounded-lg hover:bg-indigo-600"
        >
          Submit
        </button>
      </form>
    </div>
  );
};

export default DisciplinaryForm;
